import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import firebase from 'firebase/compat/app';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { EssErrorService } from 'ngx-essentia';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, filter, firstValueFrom, map, of, timeout } from 'rxjs';
import { paths } from 'functions-lib';
import { AuthService } from '../../user/auth.service';
import { isPermissionDenied } from '../../shared/services/db-errors';
import { ConnectionStatusService } from '../../shared/services/connection-status.service';
import { RETRY_PAUSE_AFTER_MAXRETRY_MS, RETRY_WAIT_FOR_CONNECTION_MS, transientTransactionAbort } from '../../shared/services/rtdb-transaction-errors';
import { ConnectionLostError } from './poll-errors';

/**
 * Role of the signed-in user on one poll (concept-poll-scoped-access-2026-10-07 §2.1, workorder 1.2 §0):
 *  - `owner`   auth.uid === ownerId
 *  - `legacy`  the id token carries a delegation of the owner (account access, legacy)
 *  - `coowner` own grant whose key equals pollAccess/{o}/{p}/ownerKey - everything incl. delete and keys
 *  - `editor`  own grant whose key equals pollAccess/{o}/{p}/editKey - header, options, all answers; no
 *              delete, no ownerKey, no editors index. Unreachable since workorder 1.9 (one organizer link,
 *              one key): no editKey is created or handed out any more; the role stays in the model so that
 *              a grant from dev test data still resolves and the rules branch can go with the legacy cleanup
 *  - `stale`   a grant exists but matches no key (rotated) - no rights, the node is still there (1.3 cleans up)
 *  - `none`    no grant
 */
export type PollRole = 'owner' | 'legacy' | 'coowner' | 'editor' | 'stale' | 'none';
export type GrantSource = 'adopt' | 'link' | 'mail';
export interface PollGrant { ownerId: string; pollId: string; key: string; source: GrantSource; grantedAt: number }
export interface VoteGrant extends PollGrant { voterId: string }
/** the strongest key the user holds on a poll (or on one answer of it) - the parts of a join link */
export interface SelfAccessLinkParts {
    kind: 'poll' | 'vote';
    ownerId: string;
    pollId: string;
    voterId?: string;
    key: string;
}

/**
 * What an (anonymous) session holds, as grant material (workorder 1.18 §A.1): an own poll or a held
 * poll grant with its key, an own answer or a held vote grant with its key.
 */
export type SessionItem =
    | { type: 'poll'; ownerId: string; pollId: string; key: string }
    | { type: 'vote'; ownerId: string; pollId: string; voterId: string; key: string };
/** the session items of `fromUid`, parked in sessionStorage across a sign-in (popup, redirect, password) */
export interface SessionStash { fromUid: string; items: SessionItem[]; createdAt: number }
/** outcome of applyStashedGrants; `failed: -1` is the caller's marker for "nothing could be collected" */
export interface AdoptResult { accepted: number; noop: number; failed: number }
/** sessionStorage key of the SessionStash */
export const ADOPT_STASH_KEY = 'adoptGrants';
/** the stash keys of the legacy merge delegation - removed when found, never written any more (1.18) */
const LEGACY_STASH_KEYS = ['delegationFromUid', 'delegationSecretKey'];
/** from this many session items on, collectSessionItems warns (workorder 1.18 D4) - no cap */
const SESSION_ITEMS_WARN_THRESHOLD = 200;

/** roles that may edit the poll (CurrentPollService.isOwner) */
export const EDIT_ROLES: readonly PollRole[] = ['owner', 'legacy', 'coowner', 'editor'];
/** roles that may delete the poll and read its key (the organizer link makes a co-organizer = coowner) */
export const MANAGE_ROLES: readonly PollRole[] = ['owner', 'legacy', 'coowner'];
/** a role that came from a grant - the only ones a key change (support rotation in the database) can take away */
const GRANT_ROLES: readonly PollRole[] = ['coowner', 'editor'];

/** the rules refused the read - the probe's "no" (never thrown, never logged as an error) */
const DENIED = Symbol('permission_denied');
/** how long resolvePollRole waits for the first emission of the own grants before it decides without them */
const GRANTS_LOADED_TIMEOUT_MS = 15000;
/** length of crypto.randomUUID() - the rules validate it */
export const ACCESS_KEY_LENGTH = 36;
/** reason of a key attempt that ended without an error but also without a key (see tryEnsureKey) */
const EMPTY_RESULT = 'empty result';

/** one attempt of ensureKey: the key, or the transient reason why there is none */
type KeyAttempt = { key: string } | { reason: string; cause?: unknown };

interface GrantsState<T> { uid: string | null; grants: Record<string, T> }
interface CachedRole { uid: string; grantKey: string; role: PollRole }

export const pollGrantId = (ownerId: string, pollId: string) => `${ownerId}-${pollId}`;
export const voteGrantId = (ownerId: string, pollId: string, voterId: string) => `${ownerId}-${pollId}-${voterId}`;

@Injectable({ providedIn: 'root' })
/**
 * Poll and vote scoped access on the client (concept §5.1, workorder 1.2 task 3): holds the own grants
 * (pollEditors/{me}, voteEditors/{me}), resolves the role on a poll, accepts grants from a join link,
 * creates the key of a poll, builds the strongest self access link and carries a session's grant material
 * across a sign-in (collectSessionItems / applyStashedGrants, adopt on login, workorder 1.18).
 *
 * Design rules (workorder §1):
 *  - the role follows from what the rules let the reader read, not from a field on the grant: ownerKey
 *    readable and equal to the grant key -> coowner, else editKey readable and equal -> editor, both
 *    denied -> stale. A denied probe is a user state (warn at most), never an error.
 *  - `root` is the state BEFORE a write: accepting = (1) own grant, (2) editors index. Never one multi-path
 *    update, never the poll node as a whole.
 *  - a key is never overwritten: creation only via "set if null" transaction. There is no rotation in the
 *    product (workorder 1.9): a support rotation is a database write on `ownerKey`, and `watchPollKey` takes
 *    the role away from every co-organizer at once.
 *  - keys are never logged, not even shortened. Paths and uids are fine.
 * Depends on AuthService and ConnectionStatusService only (never on CurrentPollService / NavigationService /
 * UserService), so that CurrentPollService -> AccessGrantService -> AuthService stays free of cycles.
 */
export class AccessGrantService {
    private readonly pollGrantsState$ = new BehaviorSubject<GrantsState<PollGrant> | undefined>(undefined);
    private readonly voteGrantsState$ = new BehaviorSubject<GrantsState<VoteGrant> | undefined>(undefined);
    /** own poll grants by `{ownerId}-{pollId}`; emits once the grants of the signed-in uid are loaded, `{}` when signed out */
    public readonly myPollGrants$: Observable<Record<string, PollGrant>> = this.pollGrantsState$.pipe(
        filter((state): state is GrantsState<PollGrant> => !!state), map(state => state.grants));
    /** own vote grants by `{ownerId}-{pollId}-{voterId}` */
    public readonly myVoteGrants$: Observable<Record<string, VoteGrant>> = this.voteGrantsState$.pipe(
        filter((state): state is GrantsState<VoteGrant> => !!state), map(state => state.grants));
    private pollGrantsListener: { ref: firebase.database.Reference; callback: (snapshot: firebase.database.DataSnapshot) => void } | null = null;
    private voteGrantsListener: { ref: firebase.database.Reference; callback: (snapshot: firebase.database.DataSnapshot) => void } | null = null;
    private authSubscription: Subscription;
    /** resolved grant roles by `{ownerId}-{pollId}`, valid for the uid and grant key they were probed with */
    private readonly roleCache = new Map<string, CachedRole>();
    /** the running applyStashedGrants, so that the auth emission and the merge method never apply the stash twice */
    private applyInFlight: Promise<AdoptResult> | null = null;
    /** the running key creation per key path - concurrent callers share one transaction (see ensureKey) */
    private readonly pendingKeys = new Map<string, Promise<string>>();

    constructor(
        private afd: AngularFireDatabase,
        private authService: AuthService,
        private logger: LoggerService,
        private errorService: EssErrorService,
        private connectionStatus: ConnectionStatusService,
    ) {
        this.subscribeAuthChanges();
    }

    // ------------------------------------------------------------------ own grants

    /** one listener per uid on pollEditors/{uid} and voteEditors/{uid}; re-attached on every uid change */
    private subscribeAuthChanges(): void {
        this.authSubscription = this.authService.afAuthUser$.pipe(
            map(user => user?.uid ?? null),
            distinctUntilChanged()
        ).subscribe(uid => this.attachGrantListeners(uid));
    }

    private attachGrantListeners(uid: string | null): void {
        this.detachGrantListeners();
        this.roleCache.clear();
        if (!uid) {
            this.pollGrantsState$.next({ uid: null, grants: {} });
            this.voteGrantsState$.next({ uid: null, grants: {} });
            return;
        }
        // undefined until the first value arrives: resolvePollRole waits for it instead of deciding "no grant"
        this.pollGrantsState$.next(undefined);
        this.voteGrantsState$.next(undefined);
        this.pollGrantsListener = this.listen<PollGrant>(`${paths.pollEditors}/${uid}`, uid, this.pollGrantsState$);
        this.voteGrantsListener = this.listen<VoteGrant>(`${paths.voteEditors}/${uid}`, uid, this.voteGrantsState$);
    }

    private listen<T>(path: string, uid: string, state$: BehaviorSubject<GrantsState<T> | undefined>) {
        const ref = this.afd.database.ref(path);
        const callback = ref.on('value', snapshot => {
            state$.next({ uid, grants: (snapshot.val() ?? {}) as Record<string, T> });
        }, (error: Error) => {
            // the server revokes the listen on a sign-out / uid switch - the normal end of a subscription
            if (isPermissionDenied(error)) {
                this.logger.warn('AccessGrantService: grants listen revoked', path);
            } else {
                this.logger.error('AccessGrantService: grants listen failed. path: ' + path, error);
            }
            state$.next({ uid, grants: {} });
        });
        return { ref, callback };
    }

    private detachGrantListeners(): void {
        this.pollGrantsListener?.ref.off('value', this.pollGrantsListener.callback);
        this.voteGrantsListener?.ref.off('value', this.voteGrantsListener.callback);
        this.pollGrantsListener = null;
        this.voteGrantsListener = null;
    }

    /** the own grant on a poll from the last emission - undefined while loading, when signed out or without grant */
    public getPollGrant(ownerId: string, pollId: string): PollGrant | undefined {
        const state = this.pollGrantsState$.value;
        if (!state || state.uid !== (this.authService.uid ?? null)) return undefined;
        return state.grants[pollGrantId(ownerId, pollId)];
    }

    public getVoteGrant(ownerId: string, pollId: string, voterId: string): VoteGrant | undefined {
        const state = this.voteGrantsState$.value;
        if (!state || state.uid !== (this.authService.uid ?? null)) return undefined;
        return state.grants[voteGrantId(ownerId, pollId, voterId)];
    }

    /** resolves once the poll and vote grants of `uid` are loaded; after the timeout with a warning, as if there were none */
    private async whenGrantsLoaded(uid: string): Promise<void> {
        const loadedFor = <T>(state$: BehaviorSubject<GrantsState<T> | undefined>) => firstValueFrom(state$.pipe(
            filter(state => !!state && state.uid === uid),
            timeout({ first: GRANTS_LOADED_TIMEOUT_MS, with: () => of(undefined) })
        ));
        const [polls, votes] = await Promise.all([loadedFor(this.pollGrantsState$), loadedFor(this.voteGrantsState$)]);
        if (!polls || !votes) {
            this.logger.warn('AccessGrantService: own grants not loaded in time, resolving without them', uid);
        }
    }

    // ------------------------------------------------------------------ role

    /**
     * Role of the signed-in user on the poll. Token first (owner, legacy - no round trip), then the own
     * grant, probed against the key paths the rules let the reader read (workorder §1 rule 1). The probe
     * result is cached per poll for the uid and grant key it was taken with; a changed grant key or
     * `invalidate()` drops it. Throws on a read failure other than permission_denied (offline).
     */
    public async resolvePollRole(ownerId: string, pollId: string): Promise<PollRole> {
        const uid = this.authService.uid;
        if (!uid) return 'none';
        if (uid === ownerId) return 'owner';
        if (this.authService.getDelegatedUserIds().includes(ownerId)) return 'legacy';
        await this.whenGrantsLoaded(uid);
        const grant = this.getPollGrant(ownerId, pollId);
        if (!grant) return 'none';
        const cached = this.getCachedPollRole(ownerId, pollId);
        if (cached) return cached;
        const role = await this.probeRoleForKey(ownerId, pollId, grant.key);
        this.cacheRole(ownerId, pollId, uid, grant.key, role);
        this.logger.log('AccessGrantService: role resolved', ownerId, pollId, role);
        return role;
    }

    /** the two read probes: ownerKey readable and equal -> coowner, else editKey readable and equal -> editor, else stale */
    private async probeRoleForKey(ownerId: string, pollId: string, key: string): Promise<PollRole> {
        const ownerKey = await this.readKeyProbe(`${this.pollAccessPath(ownerId, pollId)}/ownerKey`);
        if (ownerKey !== DENIED && ownerKey === key) return 'coowner';
        const editKey = await this.readKeyProbe(`${this.pollAccessPath(ownerId, pollId)}/editKey`);
        if (editKey !== DENIED && editKey === key) return 'editor';
        return 'stale';
    }

    private async readKeyProbe(path: string): Promise<string | null | typeof DENIED> {
        try {
            const snapshot = await this.afd.database.ref(path).get();
            return snapshot.val() as string | null;
        } catch (error) {
            if (isPermissionDenied(error)) return DENIED;
            throw error;
        }
    }

    private cacheRole(ownerId: string, pollId: string, uid: string, grantKey: string, role: PollRole): void {
        this.roleCache.set(pollGrantId(ownerId, pollId), { uid, grantKey, role });
    }

    /** the cached grant role (coowner / editor / stale), if it was probed for the current uid and grant key */
    public getCachedPollRole(ownerId: string, pollId: string): PollRole | undefined {
        const cached = this.roleCache.get(pollGrantId(ownerId, pollId));
        if (!cached) return undefined;
        const grant = this.getPollGrant(ownerId, pollId);
        if (!grant || cached.uid !== this.authService.uid || cached.grantKey !== grant.key) {
            this.roleCache.delete(pollGrantId(ownerId, pollId));
            return undefined;
        }
        return cached.role;
    }

    public invalidate(ownerId: string, pollId: string): void {
        this.roleCache.delete(pollGrantId(ownerId, pollId));
    }

    /** synchronous: owner / legacy from the token, or a cached grant role that may edit */
    public canEditPoll(ownerId: string, pollId: string): boolean {
        const uid = this.authService.uid;
        if (!uid || !ownerId) return false;
        if (uid === ownerId || this.authService.getDelegatedUserIds().includes(ownerId)) return true;
        return GRANT_ROLES.includes(this.getCachedPollRole(ownerId, pollId));
    }

    /** the guard variant: resolves the role first */
    public async canEditPollAsync(ownerId: string, pollId: string): Promise<boolean> {
        return EDIT_ROLES.includes(await this.resolvePollRole(ownerId, pollId));
    }

    /** true when the cached role is a grant role - the writer that gets PERMISSION_DENIED then holds a revoked grant, not a bug */
    public hasGrantRole(ownerId: string, pollId: string): boolean {
        return GRANT_ROLES.includes(this.getCachedPollRole(ownerId, pollId));
    }

    /**
     * Synchronous: the voter himself, a poll role that may edit, or a vote grant. The validity of a vote
     * grant is not probed - an invalid one surfaces at the write (persistVote's callers warn, never error).
     */
    public canEditVote(ownerId: string, pollId: string, voterId: string): boolean {
        const uid = this.authService.uid;
        if (!uid || !voterId) return false;
        if (voterId === uid) return true;
        if (this.canEditPoll(ownerId, pollId)) return true;
        return !!this.getVoteGrant(ownerId, pollId, voterId);
    }

    /**
     * Live watch of the key behind a grant role (workorder §1 rule 6): the ownerKey for a co-owner, the
     * editKey for an editor. Emits `valid` with every value equal to the own grant key; a different value
     * (rotated) or a revoked listen (permission_denied) emits `revoked` once and completes.
     */
    public watchPollKey(ownerId: string, pollId: string, role: 'coowner' | 'editor'): Observable<'valid' | 'revoked'> {
        return new Observable<'valid' | 'revoked'>(subscriber => {
            const grant = this.getPollGrant(ownerId, pollId);
            if (!grant) {
                subscriber.next('revoked');
                subscriber.complete();
                return undefined;
            }
            const path = `${this.pollAccessPath(ownerId, pollId)}/${role === 'coowner' ? 'ownerKey' : 'editKey'}`;
            const ref = this.afd.database.ref(path);
            const callback = ref.on('value', snapshot => {
                if (snapshot.val() === grant.key) {
                    subscriber.next('valid');
                } else {
                    this.logger.warn('AccessGrantService: key changed, grant revoked', path);
                    subscriber.next('revoked');
                    subscriber.complete();
                }
            }, (error: Error) => {
                if (isPermissionDenied(error)) {
                    this.logger.warn('AccessGrantService: key listen revoked', path);
                } else {
                    this.logger.error('AccessGrantService: key listen failed. path: ' + path, error);
                }
                subscriber.next('revoked');
                subscriber.complete();
            });
            return () => ref.off('value', callback);
        });
    }

    // ------------------------------------------------------------------ keys

    /**
     * The one key of a poll - the ownerKey behind the organizer link (workorder 1.9) -, created where
     * missing: a "set if null" transaction on the key path, never a write on the poll node (no rule there).
     * Only owner, legacy and co-owner may call it: a denied transaction is a programming error and
     * reported as such. No editKey is created any more.
     * One key per path, created once (workorder-ensure-key-disconnect-retry-2026-10-30): concurrent callers
     * for the same path share one transaction and get the same key; a transient outcome (connection drop,
     * concurrent write, no key in the result) is retried once inside that shared promise. Still no key after
     * the retry -> warning and ConnectionLostError (`isConnectionLost`), which the surfaces show as a
     * connection problem; permission_denied -> error and rethrown, never retried.
     */
    public async ensureOwnerKey(ownerId: string, pollId: string): Promise<string> {
        return this.ensureKey(`${this.pollAccessPath(ownerId, pollId)}/ownerKey`);
    }

    /** the key of one answer, created where missing; the voter, the owner, legacy and both poll grants may call it */
    public async ensureVoteKey(ownerId: string, pollId: string, voterId: string): Promise<string> {
        return this.ensureKey(`${this.voteAccessPath(ownerId, pollId, voterId)}/editKey`);
    }

    /**
     * Single-flight per key path: a second "set if null" transaction started while the first is still
     * unacknowledged sees the first one's hidden pending write (applyLocally=false), aborts locally as
     * "exists" and the read after it still returns null - the "no key after transaction" of 2026-10-27
     * (two surfaces 28 ms apart). Same pattern as UserService.ensureDbUser.
     */
    private ensureKey(path: string): Promise<string> {
        const running = this.pendingKeys.get(path);
        if (running) {
            this.logger.log('AccessGrantService.ensureKey: already running, awaiting it', path);
            return running;
        }
        const promise = this.ensureKeyWithRetry(path).finally(() => {
            if (this.pendingKeys.get(path) === promise) {
                this.pendingKeys.delete(path);
            }
        });
        this.pendingKeys.set(path, promise);
        return promise;
    }

    /** one attempt plus at most one retry after a transient outcome (the retry is normal operation: `log`) */
    private async ensureKeyWithRetry(path: string): Promise<string> {
        const first = await this.tryEnsureKey(path);
        if ('key' in first) return first.key;
        this.logger.log(`AccessGrantService.ensureKey: no key (${first.reason}), retrying once. path: ` + path);
        if (first.reason === 'disconnect' || first.reason === EMPTY_RESULT) {
            await this.connectionStatus.waitForConnected(RETRY_WAIT_FOR_CONNECTION_MS);
        } else if (first.reason === 'maxretry') {
            await new Promise(resolve => setTimeout(resolve, RETRY_PAUSE_AFTER_MAXRETRY_MS));
        }
        // 'set' / 'overriddenbyset': the write that got in the way is through - retry right away
        const second = await this.tryEnsureKey(path);
        if ('key' in second) return second.key;
        const message = `AccessGrantService.ensureKey: no key after retry (${second.reason}). path: ` + path;
        this.logger.warn(message);
        throw new ConnectionLostError(message, second.reason, second.cause);
    }

    /**
     * One attempt: the key from the transaction result, else from a read. A transaction that aborted
     * locally or a read offline (the cache stays empty with applyLocally=false) can come back without a
     * key - a transient outcome like an abort reason. permission_denied is reported and thrown, every
     * other error thrown as is.
     */
    private async tryEnsureKey(path: string): Promise<KeyAttempt> {
        const ref = this.afd.database.ref(path);
        let result: { snapshot?: firebase.database.DataSnapshot | null } | undefined;
        try {
            // applyLocally=false: the optimistic local run must not show a key the server may reject
            result = await ref.transaction(current => current === null ? this.generateKey() : undefined, undefined, false);
        } catch (error) {
            this.throwIfDenied(path, error);
            const abortReason = transientTransactionAbort(error);
            if (abortReason) return { reason: abortReason, cause: error };
            throw error;
        }
        const keyFromResult = result?.snapshot?.val();
        if (typeof keyFromResult === 'string') return { key: keyFromResult };
        try {
            const key = (await ref.get()).val();
            return typeof key === 'string' ? { key } : { reason: EMPTY_RESULT };
        } catch (error) {
            this.throwIfDenied(path, error);
            // "Client is offline." - nothing cached to read
            return { reason: EMPTY_RESULT, cause: error };
        }
    }

    private throwIfDenied(path: string, error: unknown): void {
        if (isPermissionDenied(error)) {
            this.logger.error('AccessGrantService.ensureKey: denied - the caller has no right on this poll. path: ' + path, error);
            throw error;
        }
    }

    // ------------------------------------------------------------------ accepting grants

    /**
     * Grant from a join link (workorder §1 rule 4): (1) own grant node - the rules validate the key, a
     * rejected write is the link's verdict and thrown; (2) editors index entry - UI information only, a
     * failure is a warning. Never downgrades: owner, legacy and co-owner are a no-op, so is an editor who
     * already holds this key.
     */
    public async acceptPollGrant(ownerId: string, pollId: string, key: string, source: GrantSource, displayName?: string): Promise<'granted' | 'noop'> {
        const uid = this.authService.uid;
        if (!uid) throw this.errorService.newError('acceptPollGrant: nobody signed in');
        const roleBefore = await this.resolvePollRole(ownerId, pollId);
        if (MANAGE_ROLES.includes(roleBefore)) {
            this.logger.log('AccessGrantService.acceptPollGrant: noop, already', roleBefore, ownerId, pollId);
            return 'noop';
        }
        if (roleBefore === 'editor' && this.getPollGrant(ownerId, pollId)?.key === key) {
            this.logger.log('AccessGrantService.acceptPollGrant: noop, same editor grant', ownerId, pollId);
            return 'noop';
        }
        const grantPath = `${paths.pollEditors}/${uid}/${pollGrantId(ownerId, pollId)}`;
        const grant = { ownerId, pollId, key, source, grantedAt: firebase.database.ServerValue.TIMESTAMP };
        await this.afd.database.ref(grantPath).set(grant);
        this.logger.log('AccessGrantService.acceptPollGrant: grant written', grantPath, source);
        // the role of the key just written - probed directly, independent of the listener echo
        this.invalidate(ownerId, pollId);
        const role = await this.probeRoleForKey(ownerId, pollId, key);
        this.cacheRole(ownerId, pollId, uid, key, role);
        this.logger.log('AccessGrantService.acceptPollGrant: granted as', role, ownerId, pollId);
        if (!GRANT_ROLES.includes(role)) {
            // the rules accepted the key a moment ago - a rotation in between; the index would be wrong
            this.logger.warn('AccessGrantService.acceptPollGrant: grant written but already stale', ownerId, pollId);
            return 'granted';
        }
        await this.writeEditorsIndex(ownerId, pollId, uid, role, displayName);
        return 'granted';
    }

    private async writeEditorsIndex(ownerId: string, pollId: string, uid: string, role: PollRole, displayName?: string): Promise<void> {
        const indexPath = `${this.pollAccessPath(ownerId, pollId)}/editors/${uid}`;
        const entry: { displayName?: string; role: 'owner' | 'editor'; grantedAt: unknown } = {
            role: role === 'coowner' ? 'owner' : 'editor',
            grantedAt: firebase.database.ServerValue.TIMESTAMP,
        };
        const name = (displayName ?? '').trim().slice(0, 200);
        if (name) entry.displayName = name;
        try {
            await this.afd.database.ref(indexPath).set(entry);
        } catch (error) {
            this.logger.warn('AccessGrantService.acceptPollGrant: editors index not written, grant is valid anyway', indexPath, error);
        }
    }

    /**
     * Vote grant from a join link. Written even when the caller has poll rights: the grant is the
     * My-Votes index as well (concept §5.5 source 2). No index node exists for votes.
     */
    public async acceptVoteGrant(ownerId: string, pollId: string, voterId: string, key: string, source: GrantSource): Promise<'granted' | 'noop'> {
        const uid = this.authService.uid;
        if (!uid) throw this.errorService.newError('acceptVoteGrant: nobody signed in');
        if (voterId === uid) {
            this.logger.log('AccessGrantService.acceptVoteGrant: noop, own answer', ownerId, pollId);
            return 'noop';
        }
        await this.whenGrantsLoaded(uid);
        if (this.getVoteGrant(ownerId, pollId, voterId)?.key === key) {
            this.logger.log('AccessGrantService.acceptVoteGrant: noop, same grant', ownerId, pollId, voterId);
            return 'noop';
        }
        const grantPath = `${paths.voteEditors}/${uid}/${voteGrantId(ownerId, pollId, voterId)}`;
        const grant = { ownerId, pollId, voterId, key, source, grantedAt: firebase.database.ServerValue.TIMESTAMP };
        await this.afd.database.ref(grantPath).set(grant);
        this.logger.log('AccessGrantService.acceptVoteGrant: grant written', grantPath, source);
        return 'granted';
    }

    /** own grant node and own editors index entry (1.3 cleanup of stale grants, phase 2 leaving a poll) */
    public async removeOwnPollGrant(ownerId: string, pollId: string): Promise<void> {
        const uid = this.authService.uid;
        if (!uid) throw this.errorService.newError('removeOwnPollGrant: nobody signed in');
        await this.afd.database.ref(`${paths.pollEditors}/${uid}/${pollGrantId(ownerId, pollId)}`).remove();
        this.invalidate(ownerId, pollId);
        const indexPath = `${this.pollAccessPath(ownerId, pollId)}/editors/${uid}`;
        try {
            await this.afd.database.ref(indexPath).remove();
        } catch (error) {
            this.logger.warn('AccessGrantService.removeOwnPollGrant: editors index entry not removed', indexPath, error);
        }
        this.logger.log('AccessGrantService: own poll grant removed', ownerId, pollId);
    }

    public async removeOwnVoteGrant(ownerId: string, pollId: string, voterId: string): Promise<void> {
        const uid = this.authService.uid;
        if (!uid) throw this.errorService.newError('removeOwnVoteGrant: nobody signed in');
        await this.afd.database.ref(`${paths.voteEditors}/${uid}/${voteGrantId(ownerId, pollId, voterId)}`).remove();
        this.logger.log('AccessGrantService: own vote grant removed', ownerId, pollId, voterId);
    }

    // ------------------------------------------------------------------ list support (1.3)

    /**
     * Validity of an own vote grant (workorder 1.3 task 1): the editKey of the answer readable and equal
     * to the grant key -> valid; permission_denied or another value (rotated) -> stale. Any other read
     * failure is thrown - the caller skips the entry and removes nothing on an unclear finding. No cache:
     * one probe per list load. Without an own grant there is nothing to validate -> stale.
     */
    public async probeVoteGrant(ownerId: string, pollId: string, voterId: string): Promise<'valid' | 'stale'> {
        const grant = this.getVoteGrant(ownerId, pollId, voterId);
        if (!grant) return 'stale';
        const editKey = await this.readKeyProbe(`${this.voteAccessPath(ownerId, pollId, voterId)}/editKey`);
        return editKey !== DENIED && editKey === grant.key ? 'valid' : 'stale';
    }

    /**
     * Grants held by a legacy delegation uid (concept §5.4 last paragraph): one read of
     * pollEditors/{uid}, readable for the delegate (rules test 9). permission_denied -> [] with a
     * warning (the delegation is gone meanwhile), other failures are thrown. Read only - whether to
     * adopt them is the caller's decision (MyPollsService / MyPartipationsService).
     */
    public async getLegacyPollGrants(delegatedUid: string): Promise<PollGrant[]> {
        return this.readGrantList<PollGrant>(`${paths.pollEditors}/${delegatedUid}`);
    }

    public async getLegacyVoteGrants(delegatedUid: string): Promise<VoteGrant[]> {
        return this.readGrantList<VoteGrant>(`${paths.voteEditors}/${delegatedUid}`);
    }

    private async readGrantList<T>(path: string): Promise<T[]> {
        try {
            const snapshot = await this.afd.database.ref(path).get();
            return Object.values((snapshot.val() ?? {}) as Record<string, T>);
        } catch (error) {
            if (isPermissionDenied(error)) {
                this.logger.warn('AccessGrantService: legacy grants not readable, the delegation is gone', path);
                return [];
            }
            throw error;
        }
    }

    // ------------------------------------------------------------------ adopt on login (1.18)

    /**
     * Everything the signed-in (anonymous) session `uid` holds, as grant material - runs as the source
     * user BEFORE the sign-in (workorder 1.18 §A.2, concept §5.1 / §5.4): the own polls (`header/{uid}`,
     * ownerKey created where missing), the own answers (`participation/{uid}`, vote key created where
     * missing - the own answer in an own poll as well, D7), and the held poll and vote grants with their
     * keys. One entry that cannot be keyed (poll deleted meanwhile, rules refuse) is skipped with a log
     * line, never thrown - the sign-in must not fail on the collection. Only a failing list read is thrown
     * (the caller then signs in anyway and tells the user, D2). Logs counts, never keys.
     */
    public async collectSessionItems(uid: string): Promise<SessionItem[]> {
        const items: SessionItem[] = [];
        const seen = new Set<string>();
        /** true when the item is new - an own poll held as a grant as well, or a grant listed twice, counts once */
        const add = (item: SessionItem): boolean => {
            const id = item.type === 'poll' ? `poll:${pollGrantId(item.ownerId, item.pollId)}` : `vote:${voteGrantId(item.ownerId, item.pollId, item.voterId)}`;
            if (seen.has(id)) return false;
            seen.add(id);
            items.push(item);
            return true;
        };
        const counts = { polls: 0, votes: 0, heldPoll: 0, heldVote: 0, skipped: 0 };

        // 1. own polls -> ownerKey
        const headers = await this.readChildren<{ ownerId?: string; pollId?: string }>(`${paths.header}/${uid}`);
        for (const [pollId, header] of headers) {
            if (!header || typeof header !== 'object') continue; // deleted / empty header
            try {
                add({ type: 'poll', ownerId: uid, pollId, key: await this.ensureOwnerKey(uid, pollId) });
                counts.polls++;
            } catch (error) {
                counts.skipped++;
                this.logger.log('AccessGrantService.collectSessionItems: own poll skipped, no key', uid, pollId, error?.code ?? error?.message);
            }
        }

        // 2. own answers -> vote key (also the own answer in an own poll, D7)
        const participations = await this.readChildren<{ ownerId?: string; pollId?: string }>(`${paths.participation}/${uid}`);
        for (const [, participation] of participations) {
            const ownerId = participation?.ownerId;
            const pollId = participation?.pollId;
            if (typeof ownerId !== 'string' || typeof pollId !== 'string' || !ownerId || !pollId) continue;
            try {
                add({ type: 'vote', ownerId, pollId, voterId: uid, key: await this.ensureVoteKey(ownerId, pollId, uid) });
                counts.votes++;
            } catch (error) {
                // the poll is gone (the rules refuse the key) or the read failed: not part of this session
                counts.skipped++;
                this.logger.log('AccessGrantService.collectSessionItems: own answer skipped, no key', ownerId, pollId, error?.code ?? error?.message);
            }
        }

        // 3. held grants, unchanged with their key
        const pollGrants = await this.readChildren<PollGrant>(`${paths.pollEditors}/${uid}`);
        for (const [, grant] of pollGrants) {
            if (!grant?.ownerId || !grant?.pollId || !grant?.key) continue;
            if (add({ type: 'poll', ownerId: grant.ownerId, pollId: grant.pollId, key: grant.key })) counts.heldPoll++;
        }
        const voteGrants = await this.readChildren<VoteGrant>(`${paths.voteEditors}/${uid}`);
        for (const [, grant] of voteGrants) {
            if (!grant?.ownerId || !grant?.pollId || !grant?.voterId || !grant?.key) continue;
            if (add({ type: 'vote', ownerId: grant.ownerId, pollId: grant.pollId, voterId: grant.voterId, key: grant.key })) counts.heldVote++;
        }

        this.logger.log('AccessGrantService.collectSessionItems', { ...counts, items: items.length });
        if (items.length >= SESSION_ITEMS_WARN_THRESHOLD) {
            this.logger.warn('AccessGrantService.collectSessionItems: unusually many items', uid, items.length);
        }
        return items;
    }

    /** one read of a list node as `[key, value]` pairs; `[]` when empty; a read failure is thrown */
    private async readChildren<T>(path: string): Promise<[string, T][]> {
        const snapshot = await this.afd.database.ref(path).get();
        const value = snapshot.val();
        if (!value || typeof value !== 'object') return [];
        return Object.entries(value as Record<string, T>);
    }

    /** parks the items for the sign-in (the stash survives a redirect); nothing is stashed for an empty list */
    public stashSessionItems(fromUid: string, items: SessionItem[]): void {
        if (!items.length) {
            this.clearStash();
            return;
        }
        const stash: SessionStash = { fromUid, items, createdAt: Date.now() };
        try {
            window.sessionStorage.setItem(ADOPT_STASH_KEY, JSON.stringify(stash));
            this.logger.log('AccessGrantService.stashSessionItems', fromUid, items.length);
        } catch (error) {
            // private mode / blocked storage: the popup and password paths still have the items in memory
            this.logger.warn('AccessGrantService.stashSessionItems: sessionStorage not writable', error?.message ?? error);
        }
    }

    /** the parked stash, or null when there is none or the storage cannot be read / holds garbage */
    public readStash(): SessionStash | null {
        try {
            // the keys of the legacy merge delegation: left behind by a session that started before 1.18
            for (const key of LEGACY_STASH_KEYS) window.sessionStorage.removeItem(key);
            const raw = window.sessionStorage.getItem(ADOPT_STASH_KEY);
            if (!raw) return null;
            const stash = JSON.parse(raw) as SessionStash;
            if (!stash || typeof stash.fromUid !== 'string' || !Array.isArray(stash.items)) {
                this.logger.warn('AccessGrantService.readStash: stash unreadable, dropped');
                this.clearStash();
                return null;
            }
            return stash;
        } catch (error) {
            this.logger.warn('AccessGrantService.readStash: sessionStorage not readable', error?.message ?? error);
            return null;
        }
    }

    public clearStash(): void {
        try {
            window.sessionStorage.removeItem(ADOPT_STASH_KEY);
        } catch (error) {
            this.logger.warn('AccessGrantService.clearStash: sessionStorage not writable', error?.message ?? error);
        }
    }

    /**
     * Applies the stash to the signed-in target user - runs AFTER the sign-in (workorder 1.18 §A.4): one
     * `acceptPollGrant` / `acceptVoteGrant` with source `adopt` per item, each on its own (`allSettled`,
     * D3 - a deleted poll or a rotated key must not block the others). A stash of the target uid itself is
     * neither applied nor cleared: after a redirect reload firebase restores the anonymous source user
     * first and only then resolves the redirect result, so the stash waits for the target user's emission.
     * The stash is cleared after the run, also on partial failure (a retry would fail the same way; the
     * caller tells the user). A second call while one is running waits for it and reports nothing - the
     * auth emission and the merge method both call here, the first one does the work.
     */
    public async applyStashedGrants(targetUid: string): Promise<AdoptResult> {
        const nothing: AdoptResult = { accepted: 0, noop: 0, failed: 0 };
        if (this.applyInFlight) {
            await this.applyInFlight;
            return nothing;
        }
        const stash = this.readStash();
        if (!stash) return nothing;
        if (stash.fromUid === targetUid) {
            this.logger.log('AccessGrantService.applyStashedGrants: still the source user, waiting for the target user');
            return nothing;
        }
        const run = this.applyItems(stash, targetUid);
        this.applyInFlight = run;
        try {
            return await run;
        } finally {
            this.applyInFlight = null;
        }
    }

    private async applyItems(stash: SessionStash, targetUid: string): Promise<AdoptResult> {
        const result: AdoptResult = { accepted: 0, noop: 0, failed: 0 };
        const displayName = this.authService.displayName;
        const outcomes = await Promise.allSettled(stash.items.map(item => item.type === 'poll'
            ? this.acceptPollGrant(item.ownerId, item.pollId, item.key, 'adopt', displayName)
            : this.acceptVoteGrant(item.ownerId, item.pollId, item.voterId, item.key, 'adopt')));
        outcomes.forEach((outcome, index) => {
            const item = stash.items[index];
            if (outcome.status === 'fulfilled') {
                if (outcome.value === 'granted') result.accepted++; else result.noop++;
            } else {
                result.failed++;
                this.logger.log('AccessGrantService.applyStashedGrants: item not adopted', item.type, item.ownerId, item.pollId,
                    item.type === 'vote' ? item.voterId : '', outcome.reason?.code ?? outcome.reason?.message ?? outcome.reason);
            }
        });
        this.clearStash();
        if (result.failed) {
            this.logger.warn('AccessGrantService.applyStashedGrants: not everything adopted', { from: stash.fromUid, to: targetUid, ...result });
        } else {
            this.logger.log('AccessGrantService.applyStashedGrants', { from: stash.fromUid, to: targetUid, ...result });
        }
        return result;
    }

    // ------------------------------------------------------------------ self access link

    /**
     * The organizer link dialog is open to every role and hands out the strongest key the user holds (concept
     * §2.3 Z2, §5.2; revision 2026-10-18): owner / legacy / co-owner -> the ownerKey (created where missing) -
     * the organizer link, the same for the dialog, the mail and the calendar (workorder 1.9: one key, no cap);
     * an editor grant from old dev data -> the key of the own grant; otherwise the vote key of `voterId` when
     * that is the own answer or a held vote grant. Null when the user holds nothing on this poll. Wired by
     * 1.4: copy-link dialog and calendar; 1.5 adds the mail queue.
     */
    public async getStrongestSelfAccessLink(ownerId: string, pollId: string, voterId?: string): Promise<SelfAccessLinkParts | null> {
        const role = await this.resolvePollRole(ownerId, pollId);
        if (MANAGE_ROLES.includes(role)) {
            const ownerKey = await this.ensureOwnerKey(ownerId, pollId);
            return { kind: 'poll', ownerId, pollId, key: ownerKey };
        }
        if (role === 'editor') {
            return { kind: 'poll', ownerId, pollId, key: this.getPollGrant(ownerId, pollId).key };
        }
        const uid = this.authService.uid;
        if (!voterId || !uid) return null;
        if (voterId === uid) {
            const key = await this.ensureVoteKey(ownerId, pollId, voterId);
            return { kind: 'vote', ownerId, pollId, voterId, key };
        }
        const voteGrant = this.getVoteGrant(ownerId, pollId, voterId);
        if (voteGrant) {
            return { kind: 'vote', ownerId, pollId, voterId, key: voteGrant.key };
        }
        return null;
    }

    // ------------------------------------------------------------------ helpers

    private pollAccessPath(ownerId: string, pollId: string): string {
        return `${paths.pollAccess}/${ownerId}/${pollId}`;
    }

    private voteAccessPath(ownerId: string, pollId: string, voterId: string): string {
        return `${paths.voteAccess}/${ownerId}/${pollId}/${voterId}`;
    }

    /** crypto.randomUUID() - 36 characters, the rules validate the length (Trello 2052: never Math.random) */
    protected generateKey(): string {
        return globalThis.crypto.randomUUID();
    }
}
